import { isUndefined } from '../../../models/utils';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, EntityType } from '../../../models/enums';
import { ExportState, ExportStream, NearDuplicateState } from '../../../models/export-stream';
import { ExportDuplicateMode } from '../../../models/export-stream-options';
import { ExportVolume, ExportVolumeDedupeState } from '../../../models/export-volume';
import { NavigationInfo } from '../../../models/navigation-info';
import { ActionType, ObjectActionListType, Permissions } from '../../../models/permission';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';

const exportStreamContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'New Export Volume ...',
        iconClass: 'fa-plus', iconColor: 'color-green-04',
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Export All Staged',
        iconClass: 'fa-play', iconColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => (item.data as ExportStream).childVolumesCount > 0 &&
            ((item.data as ExportStream).state === ExportState.OPEN || (item.data as ExportStream).state === ExportState.STAGED),
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Generate Load File ...',
        enabled: (item: GenericTreeNode) => (((item.data as ExportStream).childVolumesCount > 0) &&
            (((item.data as ExportStream).state === ExportState.STAGED) || ((item.data as ExportStream).state === ExportState.EXPORTED))),
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Export Duplicates Volume ...',
        enabled: (item: GenericTreeNode) => (item.data as ExportStream).options &&
            (item.data as ExportStream).options.exportDuplicateMode === ExportDuplicateMode.UNIQUE_DOCUMENTS,
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Create Near-Duplicate Metadata ...',
        enabled: (item: GenericTreeNode) => (item.data as ExportStream).documentCount > 0 && (item.data as ExportStream).nearDuplicateState === NearDuplicateState.INCOMPLETE,
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Update Custodians',
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]

    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }],
        enabled: (item: GenericTreeNode) => !isUndefined((item.data as ExportStream).name)
    }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.DELETE] }]
    })
];

const exportVolumeContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Export',
        iconClass: 'fa-play', iconColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => (item.data as ExportVolume).exportVolumeDisplayState === ExportState.STAGED,
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Update ...',
        iconClass: 'fa-retweet', iconColor: 'color-grey-10',
        enabled: (item: GenericTreeNode) => (item.data as ExportVolume).exportVolumeDisplayState === ExportState.EXPORTED,
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Exclude from Export Stream',
        iconClass: 'fa-check', iconColor: 'color-blue-grey-04', iconSecondClass: 'fa-minus', iconSecondColor: 'color-red-05', secondIconCustomClass: true,
        visible: (item: GenericTreeNode) => (item.data as ExportVolume).exportVolumeDedupeState === ExportVolumeDedupeState.Included,
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Include in Export Stream',
        iconClass: 'fa-check', iconColor: 'color-blue-grey-04', iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04', secondIconCustomClass: true,
        visible: (item: GenericTreeNode) => (item.data as ExportVolume).exportVolumeDedupeState === ExportVolumeDedupeState.Excluded,
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Generate Load File ...',
        enabled: (item: GenericTreeNode) => ((item.data as ExportVolume).exportVolumeDisplayState === ExportState.EXPORTED ||
            (item.data as ExportVolume).exportVolumeDisplayState === ExportState.STAGED),
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
        enabled: (item: GenericTreeNode) => (item.parent && (item.parent.children.indexOf(item) === item.parent.children.length - 1) &&
            ((item.data as ExportVolume).exportVolumeDisplayState === ExportState.EXPORTED ||
                (item.data as ExportVolume).exportVolumeDisplayState === ExportState.STAGED)),
        permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.DELETE] }]
    })
];

export const ExportMenu = new GenericTreeNode({
    name: 'Exports',
    entityType: EntityType.export,
    automationId: 'nav-tree-exports',
    navigationInfo: new NavigationInfo({ path: 'summary' }),
    permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM }],
    isExpandable: true,
    isExpandedOnSelect: true,
    isSortable: true,

    requestInfo: new DrRequestInfo({
        endpoint: Endpoint.workingSetManager_listExportStreams,
        responseName: 'exportStreams',
        data: {
            filters: [
                { attribute: 'reviewProductions', value: 'false', operator: 'EQUALS' },
                { attribute: 'reviewStream', value: 'false', operator: 'EQUALS' }
            ]
        }
    }),

    createChild: (data: any, handler?: any): GenericTreeNode => {
        const item = new ExportStream(data);
        return new GenericTreeNode({
            name: item.name,
            automationId: 'export-stream-' + item.name,
            entityType: EntityType.export,
            domId: `node_${item?.handle}`,
            permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM }],
            navigationInfo: new NavigationInfo(
                { name: 'All Docs', path: 'document-list', default: true }, { name: 'Settings', path: 'export-stream-volume-settings' }),
            customNumber: item.documentCount,
            childPadding: 0,
            icon: 'fa-cube',
            iconColor: 'color-blue-01',
            isExpandable: item.childVolumesCount > 0,
            hasExpanderPadding: true,
            isExpandedOnSelect: true,
            contextMenuOptions: exportStreamContextMenuOptions,
            onContextMenuAction: ExportMenu.onContextMenuAction,

            buildRequestInfo: (node: GenericTreeNode) => {
                if (node?.data) {
                    node.requestInfo = new DrRequestInfo({
                        endpoint: Endpoint.exportStreamManager_listExportVolumes,
                        responseName: 'exportVolumes',
                        data: {
                            exportStreamHandle: node.data.handle,
                            startIndex: 0,
                            count: 50
                        }
                    });
                }
            },
            createChild: (dataChild: any, childHandler?: any): GenericTreeNode => {
                const itemChild = new ExportVolume(dataChild);
                return new GenericTreeNode({
                    name: itemChild.name,
                    automationId: 'export-stream-' + itemChild.handle,
                    entityType: EntityType.exportVolume,
                    domId: `node_${itemChild?.handle}`,
                    navigationInfo: new NavigationInfo(
                        { name: 'All Docs', path: 'document-list', default: true },
                        { name: 'Settings', path: 'export-stream-volume-settings' },
                        { name: 'Reports', path: 'reports' }),
                    customNumber: itemChild.documentCount,
                    childPadding: 7.5,
                    hasExpanderPadding: true,
                    hasFadedNodeName: itemChild.exportVolumeDedupeState === ExportVolumeDedupeState.Included ? false : true,
                    icon: itemChild.exportVolumeDedupeState === ExportVolumeDedupeState.Included ? 'fa-check': '',
                    iconColor: 'color-blue-01',
                    contextMenuOptions: exportVolumeContextMenuOptions,
                    onContextMenuAction: ExportMenu.onContextMenuAction,
                    data: itemChild
                }, childHandler);
            },
            data: item
        }, handler);
    },

    showContextMenuButton: true,
    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New Export Stream ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04',
            permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }],
            visible: (item: GenericTreeNode) =>!item?.isReefReviewProject
        }),
        new ContextMenuOption({
            name: 'New Reef Review Export Stream ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04',
            permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }],
            visible: (item: GenericTreeNode) => item?.isReefReviewProject
        }),
        new ContextMenuOption({
            name: 'New Reef ECA Export Stream ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04',
            permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM, permissionActions: [ActionType.CREATE] }],
            visible: (item: GenericTreeNode) => item?.isReefReviewProject
        })
    ]
});
